import React, { useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { usePrintActions } from "../../store/reportsStore";
import { mapCoursePeriodSectionsSortedByQuarter } from "../../../../js/Mappers";
import { getCoursePeriodIdForPeriod } from "../../../../js/PeriodHelpers";
import MagnetboardSection from "./MagnetboardSection";
import { getDiagnosticTimestamp, logMagnetboardDiagnostic, startMagnetboardDiagnosticTimer } from "../utils/diagnostics";

const PRINT_ROW_LABEL_WIDTH = 120;

const MagnetboardPrintByTeacher = React.memo(
  ({
    filteredDisplaySchedule,
    scheduleData: {
      scheduleStructureData,
      sections,
      setSections,
      subsections,
      departments,
      schoolInfo,
      scheduleInfo,
      currentScheduleVersionID,
      scheduleType,
      currentViewType,
      currentDay,
      periodsToShow,
      teachers,
      courses,
      classrooms,
      labels,
      labs_only_filtered,
      non_labs_only_filtered,
      singletons_filtered,
      doubletons_filtered,
      no_classroom_filtered,
      has_classroom_filtered,
      sectionsById,
      subsectionParentById,
      labelsById,
    },
  }) => {
    const printByTeacherRef = useRef(null);
    const renderCountRef = useRef(0);
    const renderStartedAt = getDiagnosticTimestamp();
    const renderTimerEnd = startMagnetboardDiagnosticTimer("print-by-teacher-render", {
      rows: filteredDisplaySchedule.length,
      periods: periodsToShow.length,
      sections: sections.length,
      subsections: subsections.length,
    });
    renderCountRef.current += 1;
    const { setPrintByTeacherRef } = usePrintActions();

    useEffect(() => {
      setPrintByTeacherRef(printByTeacherRef);
      return () => setPrintByTeacherRef(null);
    }, [setPrintByTeacherRef]);

    useEffect(() => {
      logMagnetboardDiagnostic("print-by-teacher-commit", {
        render_count: renderCountRef.current,
        duration_ms: Math.round(getDiagnosticTimestamp() - renderStartedAt),
        rows: filteredDisplaySchedule.length,
        periods: periodsToShow.length,
        sections: sections.length,
        teachers_to_display: teachersToDisplay.length,
      });
    });

    const rowStyle = useMemo(
      () => ({
        gridTemplateColumns: `${PRINT_ROW_LABEL_WIDTH}px repeat(${periodsToShow.length}, minmax(0, 1fr))`,
      }),
      [periodsToShow.length]
    );

    const departmentsById = useMemo(
      () =>
        departments.reduce((result, department) => {
          result[department.department_id] = department.department;
          return result;
        }, {}),
      [departments]
    );

    const subsectionParents = useMemo(
      () => subsectionParentById || {},
      [subsectionParentById]
    );

    const periodType = useMemo(() => {
      if (scheduleType === "daily" || currentViewType === "day") return "schedule_periods";
      if (scheduleType === "ab" || scheduleType === "ab_skinny") return "day_course_periods";
      return "course_periods";
    }, [currentViewType, scheduleType]);

    const getNormalizedPeriodId = (period_info, fallback_index = null) => {
      if (!period_info) return fallback_index;
      return (
        period_info.period_id ||
        period_info.schedule_period_id ||
        period_info.course_period_id ||
        fallback_index
      );
    };

    const getTeacherSections = (entityId) =>
      sections.filter(
        (section) =>
          section.teacher_id === entityId ||
          (Array.isArray(section.secondary_teachers) &&
            section.secondary_teachers.includes(entityId))
      );

    const getCellSections = (entityId, coursePeriodId) => {
      if (!coursePeriodId) return [];

      const dataSections = getTeacherSections(entityId);

      const uniqueSections = dataSections.reduce(
        (result, section) => {
          const mainSectionId = subsectionParents[section.section_id] || section.section_id;
          const alreadyAdded = result.loggedSectionIds.has(mainSectionId);

          if (
            !alreadyAdded &&
            Array.isArray(section.course_periods) &&
            section.course_periods.includes(coursePeriodId)
          ) {
            result.loggedSectionIds.add(mainSectionId);
            result.sections.push(section);
          }

          return result;
        },
        { loggedSectionIds: new Set(), sections: [] }
      );

      return mapCoursePeriodSectionsSortedByQuarter(uniqueSections.sections);
    };

    const getTeacherDepartments = (entity) => {
      const entityDepartments = entity.display_info?.departments || [];
      return entityDepartments
        .map((departmentId) => departmentsById[departmentId])
        .filter(Boolean)
        .join(", ");
    };

    const teachersToDisplay = useMemo(() =>
      filteredDisplaySchedule.filter((entity) =>
        sections.some(
          (section) =>
            (section.teacher_id === entity.data_id ||
              (Array.isArray(section.secondary_teachers) &&
                section.secondary_teachers.includes(entity.data_id))) &&
            Array.isArray(section.course_periods) &&
            section.course_periods.length > 0
        )
      ),
      [filteredDisplaySchedule, sections]
    );

    const renderedSchedule = (
      <DndProvider backend={HTML5Backend}>
        <div
          ref={printByTeacherRef}
          className="mb-print-screen-hidden"
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            width: "1400px",
            padding: "0 12px 12px 12px",
            backgroundColor: "#fff",
          }}
        >
          {teachersToDisplay.map((entity, entityIndex) => (
            <div
              key={entity.data_id || `print-teacher-${entityIndex}`}
              className="mb-print-shell mb-print-teacher-page"
            >
              <div className="mb-print-header">
                <div className="mb-print-title">{entity.display_name}</div>
                <div className="mb-print-subtitle">{getTeacherDepartments(entity)}</div>
              </div>

              <div className="mb-row mb-middle-header mb-print-header-row" style={rowStyle}>
                <div className="mb-main-inner-box">Teacher</div>
                {periodsToShow.map((periodInfo, periodIndex) => {
                  const periodId = getNormalizedPeriodId(periodInfo, periodIndex);
                  return (
                    <div className="mb-inner-box" key={`print-header-${entity.data_id}-${periodId}`}>
                      <div>{periodInfo.period_name}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-row mb-schedule-row mb-print-schedule-row" style={rowStyle}>
                <div className="mb-schedule-main-inner-box">
                  <div className="mb-schedule-row-name-container">
                    <div className="mb-schedule-row-name">{entity.display_name}</div>
                    <div className="mb-schedule-row-department dark-gray-text">
                      {getTeacherDepartments(entity)}
                    </div>
                  </div>
                </div>

                {periodsToShow.map((periodInfo, periodIndex) => {
                  const periodId = getNormalizedPeriodId(periodInfo, periodIndex);
                  const coursePeriodId = getCoursePeriodIdForPeriod(
                    scheduleStructureData,
                    periodId,
                    {
                      period_type: periodType,
                      schedule_type: scheduleType,
                      current_day: currentDay,
                    }
                  );

                  const cellSections = getCellSections(entity.data_id, coursePeriodId);

                  return (
                    <div
                      className="mb-inner-box mb-schedule-inner-box mb-print-schedule-inner-box"
                      key={`${entity.data_id || entityIndex}-${periodId}`}
                    >
                      {cellSections.map((sectionInfo, sectionIndex) => (
                        <MagnetboardSection
                          key={`${entity.data_id || entityIndex}-${periodId}-${sectionInfo.section_id || sectionInfo.subsection_id || sectionIndex}`}
                          schoolInfo={schoolInfo}
                          scheduleInfo={scheduleInfo}
                          scheduleStructureData={scheduleStructureData}
                          scheduleType={scheduleType}
                          currentViewType={currentViewType}
                          currentView="teacher"
                          currentDay={currentDay}
                          currentScheduleVersionID={currentScheduleVersionID}
                          teachers={teachers}
                          courses={courses}
                          classrooms={classrooms}
                          sections={sections}
                          setSections={setSections}
                          subsections={subsections}
                          labels={labels}
                          setConflictsLoading={() => {}}
                          labsOnlyFiltered={labs_only_filtered}
                          nonLabsOnlyFiltered={non_labs_only_filtered}
                          singletonsFiltered={singletons_filtered}
                          doubletonsFiltered={doubletons_filtered}
                          noClassroomFiltered={no_classroom_filtered}
                          hasClassroomFiltered={has_classroom_filtered}
                          toggleEditSection={() => {}}
                          dataID={entity.data_id}
                          coursePeriodID={coursePeriodId}
                          sectionID={sectionInfo.section_id}
                          studentID={null}
                          sectionType="section"
                          handleSelectQuickViewDataID={null}
                          selectedQuickViewSectionID={null}
                          toggleLockSection={() => {}}
                          isDraggable={false}
                          isEditable={false}
                          isLockable={false}
                          isQuickViewEnabled={false}
                          isPrintMode={false}
                          magnetboardOptions={{ show_section_conflict_indicators: false }}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DndProvider>
    );

    renderTimerEnd({
      teachers_to_display: teachersToDisplay.length,
    });
    return renderedSchedule;
  }
);

export default MagnetboardPrintByTeacher;
